import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  getDoc,
  doc,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import "../styles/dashboard.css";

export default function Dashboard() {
  const [candidates, setCandidates] = useState([]);
  const [searchRoll, setSearchRoll] = useState("");
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [savingCpiId, setSavingCpiId] = useState(null);
  const [cpiMap, setCpiMap] = useState({});
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [adminProfile, setAdminProfile] = useState(null);
  const [adminRole, setAdminRole] = useState(null);

  // 🔒 Auth check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate("/login");
        return;
      }

      setUser(currentUser);

      try {
        // 🔥 Fetch profile (name, email)
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          alert("User profile missing");
          return;
        }

        setAdminProfile(userSnap.data());

        // 🔐 Check SuperAdmin first
        const superAdminRef = doc(db, "SuperAdmin", currentUser.uid);
        const superAdminSnap = await getDoc(superAdminRef);

        if (superAdminSnap.exists()) {
          setAdminRole("SuperAdmin");
          return;
        }

        // 🔐 Check Admin
        const adminRef = doc(db, "Admin", currentUser.uid);
        const adminSnap = await getDoc(adminRef);

        if (adminSnap.exists()) {
          setAdminRole("Admin");
          return;
        }

        // ❌ Not authorized
        alert("You are not authorized to access this portal.");
        navigate("/login");
      } catch (err) {
        console.error("Role resolution failed:", err);
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // 🚀 Fetch only basic candidate info initially (fast)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("role", "==", "Candidate"));
        const snapshot = await getDocs(q);

        const candidateData = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            // supporters NOT loaded yet
            proposers: [],
            seconders: [],
            campaigners: [],
          };
        });

        setCandidates(candidateData);
        setFilteredCandidates(candidateData);

        const cpiValues = {};
        candidateData.forEach((c) => {
          cpiValues[c.id] = c.cpi || "";
        });
        setCpiMap(cpiValues);
      } catch (err) {
        console.error("Error fetching candidates:", err);
      }
    };

    fetchData();
  }, []);

  // 🔍 Search roll + fetch supporters only for that candidate
  // 🔍 Search roll number with FRESH Firestore data
  const handleSearch = async () => {
    const roll = searchRoll.trim();
    if (!roll) {
      setFilteredCandidates(candidates);
      return;
    }

    try {
      // 🔥 Fresh Firestore query
      const usersRef = collection(db, "users");
      const q = query(
        usersRef,
        where("role", "==", "Candidate"),
        where("roll", "==", roll)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setFilteredCandidates([]);
        return;
      }

      const docSnap = snapshot.docs[0];
      const candidate = {
        id: docSnap.id,
        ...docSnap.data(),
      };

      // 🔥 Fetch supporters freshly
      const [proposerSnap, seconderSnap, campaignerSnap] = await Promise.all([
        getDocs(collection(db, "users", candidate.id, "proposers")),
        getDocs(collection(db, "users", candidate.id, "seconder")),
        getDocs(collection(db, "users", candidate.id, "campaigner")),
      ]);

      const detailedCandidate = {
        ...candidate,
        proposers: proposerSnap.docs.map((d) => d.data()),
        seconders: seconderSnap.docs.map((d) => d.data()),
        campaigners: campaignerSnap.docs
          .map((d) => d.data())
          .filter((c) => c.status === "Accepted"),
      };

      // 🔁 Sync CPI input with fresh data
      setCpiMap((prev) => ({
        ...prev,
        [candidate.id]: candidate.cpi || "",
      }));

      setFilteredCandidates([detailedCandidate]);
    } catch (err) {
      console.error("Error searching candidate:", err);
    }
  };

  // ✅ Update candidature
  const updateCandidature = async (id, status) => {
    if (!adminProfile) {
      alert("Admin profile not loaded. Please refresh.");
      return;
    }

    setLoadingId(id);
    const userRef = doc(db, "users", id);

    try {
      await updateDoc(userRef, {
        candidature: status,
        decisionBy: {
          uid: user.uid,
          name: adminProfile.name,
          email: adminProfile.email,
          at: new Date(),
          role: adminProfile.role,
        },
      });

      if (status === "Rejected") {
        try {
          const token = await auth.currentUser.getIdToken(true);

          await fetch(
            "https://us-central1-ec-nomination-portal.cloudfunctions.net/candidatureMail",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                candidateId: id,
                decision: "Rejected",
              }),
            }
          );
        } catch (err) {
          console.error("Rejection mail failed:", err);
        }
      }

      const updateFn = (arr) =>
        arr.map((c) =>
          c.id === id
            ? {
                ...c,
                candidature: status,
                decisionBy: {
                  name: adminProfile.name,
                  email: adminProfile.email,
                },
              }
            : c
        );

      setFilteredCandidates((prev) => updateFn(prev));
      setCandidates((prev) => updateFn(prev));
    } catch (err) {
      console.error("Error updating candidature:", err);
    } finally {
      setLoadingId(null);
    }
  };

  // ✏️ Handle CPI input
  const handleCpiChange = (id, value) => {
    // Allow empty input (for editing)
    if (value === "") {
      setCpiMap((prev) => ({ ...prev, [id]: "" }));
      return;
    }

    // Allow only numbers with up to 2 decimals
    if (!/^\d{0,2}(\.\d{0,2})?$/.test(value)) return;

    const num = Number(value);

    // Enforce range: 0 ≤ CPI ≤ 10
    if (num < 0 || num > 10) return;

    setCpiMap((prev) => ({ ...prev, [id]: value }));
  };

  // 💾 Save CPI
  const saveCpi = async (id) => {
    const cpiValue = parseFloat(cpiMap[id]);
    if (isNaN(cpiValue)) return;

    setSavingCpiId(id);
    const userRef = doc(db, "users", id);

    try {
      await updateDoc(userRef, { cpi: cpiValue });

      const updateFn = (arr) =>
        arr.map((c) => (c.id === id ? { ...c, cpi: cpiValue } : c));

      setFilteredCandidates((prev) => updateFn(prev));
      setCandidates((prev) => updateFn(prev));
    } catch (err) {
      console.error("Error saving CPI:", err);
    } finally {
      setSavingCpiId(null);
    }
  };

  // ✅ Save CPI + Accept candidature together
  const acceptWithCpi = async (id) => {
    const existingCandidate = candidates.find((c) => c.id === id);
    const finalCpi = !isNaN(parseFloat(cpiMap[id]))
      ? parseFloat(cpiMap[id])
      : existingCandidate?.cpi;

    if (finalCpi == null) {
      alert("CPI is required to accept candidature.");
      return;
    }

    if (!adminProfile) {
      alert("Admin profile not loaded. Please refresh.");
      return;
    }

    setLoadingId(id);
    const userRef = doc(db, "users", id);

    try {
      await updateDoc(userRef, {
        cpi: finalCpi,
        candidature: "Accepted",
        decisionBy: {
          uid: user.uid,
          name: adminProfile.name,
          email: adminProfile.email,
          role: adminRole,
          eSign: true,
          at: new Date(),
        },
      });

      // 📧 Send nomination verification mail
      try {
        const token = await auth.currentUser.getIdToken();

        await fetch(
          "https://us-central1-ec-nomination-portal.cloudfunctions.net/candidatureMail",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              candidateId: id,
            }),
          }
        );
      } catch (mailErr) {
        console.error("Mail sending failed:", mailErr);
      }

      const updateFn = (arr) =>
        arr.map((c) =>
          c.id === id
            ? {
                ...c,
                cpi: finalCpi,
                candidature: "Accepted",
                decisionBy: {
                  name: adminProfile.name,
                  email: adminProfile.email,
                },
              }
            : c
        );

      setFilteredCandidates((prev) => updateFn(prev));
      setCandidates((prev) => updateFn(prev));
    } catch (err) {
      console.error("Error accepting candidature:", err);
    } finally {
      setLoadingId(null);
    }
  };

  if (!user) return <p>Loading...</p>;

  return (
    <div className="dashboard">
      <h2>Admin Dashboard</h2>

      {/* Search Bar */}
      <div className="search-bar">
        <input
          type="text"
          placeholder="Exact Roll Number to get full details"
          value={searchRoll}
          onChange={(e) => setSearchRoll(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearch();
            }
          }}
        />
        <button onClick={handleSearch}>Search</button>
      </div>

      {filteredCandidates.length === 0 && (
        <p>No candidates found with roll: {searchRoll}</p>
      )}

      {/* Candidate Cards */}
      {filteredCandidates.map((c) => (
        <div key={c.id} className="candidate-card candidate-card-flex">
          <div className="candidate-left">
            <h3>{c.name}</h3>
            <p>
              <strong>Email Status:</strong> {c.emailStatus} <br />
              <strong>Post:</strong> {c.post} <br />
              <strong>Email:</strong> {c.email} <br />
              <strong>Roll No:</strong> {c.roll} <br />
              <strong>Phone:</strong> {c.mobileNumber || "N/A"} <br />
              <strong>Survey Link:</strong>{" "}
              {c.gform ? (
                <a
                  href={
                    c.gform.startsWith("http://") ||
                    c.gform.startsWith("https://")
                      ? c.gform
                      : `https://${c.gform}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {c.gform}
                </a>
              ) : (
                "N/A"
              )}
              <br />
              <strong>CPI:</strong> {c.cpi} <br />
              <strong>Candidature:</strong>{" "}
              {c.candidature ? (
                <>
                  <span
                    style={{
                      color:
                        c.candidature === "Accepted"
                          ? "#3f51b5"
                          : c.candidature === "Rejected"
                          ? "rgb(145, 14, 0)"
                          : "black",
                      fontWeight: "bold",
                    }}
                  >
                    {c.candidature.toUpperCase()}
                  </span>
                  {c.decisionBy && (
                    <>
                      <br />
                      <strong>By:</strong> {c.decisionBy.name}
                    </>
                  )}
                </>
              ) : (
                "Pending"
              )}
            </p>

            {/* CPI input + Save */}
            {c.candidature !== "Accepted" &&
              c.candidature !== "Rejected" &&
              c.cpi == null && (
                <div className="cpi-input">
                  <label>
                    <strong>CPI: </strong>
                    <input
                      type="text"
                      value={cpiMap[c.id] || ""}
                      onChange={(e) => handleCpiChange(c.id, e.target.value)}
                      placeholder="e.g., 8.75"
                      required
                    />
                  </label>
                  <button
                    onClick={() => saveCpi(c.id)}
                    disabled={savingCpiId === c.id}
                  >
                    {savingCpiId === c.id ? "Saving..." : "Save CPI"}
                  </button>
                </div>
              )}

            {/* Accept / Reject Buttons */}
            {c.candidature !== "Accepted" && c.candidature !== "Rejected" && (
              <div className="action-buttons">
                <button
                  className="accept-btn"
                  onClick={() => acceptWithCpi(c.id)}
                  disabled={loadingId === c.id}
                >
                  {loadingId === c.id ? "Processing..." : "ACCEPT"}
                </button>
                <button
                  className="reject-btn"
                  onClick={() => updateCandidature(c.id, "Rejected")}
                  disabled={loadingId === c.id}
                >
                  {loadingId === c.id ? "Processing..." : "REJECT"}
                </button>
              </div>
            )}

            {/* Show supporters only if searched */}
            {c.proposers.length > 0 && (
              <div className="support-list">
                <p>
                  <strong>Proposers ({c.proposers.length}):</strong>
                </p>
                <ul>
                  {c.proposers.map((p, i) => (
                    <li key={i}>
                      {p.name}: {p.roll} <br />
                      {p.email}: {p.emailStatus} <br />
                      <b>Status:</b> {p.status}
                    </li>
                  ))}
                </ul>

                <p>
                  <strong>Seconders ({c.seconders.length}):</strong>
                </p>
                <ul>
                  {c.seconders.map((s, i) => (
                    <li key={i}>
                      {s.name}: {s.roll} <br />
                      {s.email}: {s.emailStatus} <br />
                      <b>Status:</b> {s.status}
                    </li>
                  ))}
                </ul>

                <p>
                  <strong>Campaigners ({c.campaigners.length}):</strong>
                </p>
                <ul>
                  {c.campaigners.map((ca, i) => (
                    <li key={i}>
                      {ca.name}: {ca.roll} <br />
                      {ca.email}: {ca.emailStatus} <br />
                      <b>Status:</b> {ca.status}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="candidate-right">
            <div className="profile-pic-wrapper">
              <img
                src={c.imageURL || "/default-profile.svg"}
                alt="Candidate Profile"
                className="profile-pic"
                onError={(e) => {
                  e.target.src = "/default-profile.svg";
                }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
